import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Heart, MessageCircle, Share2, Bookmark, Music2, 
  Plus, ChefHat, Volume2, VolumeX, Play, Pause,
  Flame, MapPin, Eye, Send, X, ChevronUp, 
  ChevronDown, Sparkles, Users, MoreHorizontal, Flag
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ReportDialog from '../components/moderation/ReportDialog';
import { toast } from 'sonner';

// Video Card Component - Optimized for Feed
const VideoCard = ({ 
  video, 
  isActive, 
  onLike, 
  onSave, 
  onShare,
  onFollow,
  onComment,
  onReport,
  isMuted,
  onToggleMute,
  index,
  total,
  t
}) => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isActive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const prog = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(prog);
    }
  };

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  return (
    <div className="relative w-full h-full bg-black flex">
      {/* Video Container - Full Width on Mobile, Side Panel on Desktop */}
      <div className="flex-1 relative flex items-center justify-center bg-black">
        {/* Video Element */}
        <video
          ref={videoRef}
          src={video.video_url}
          poster={video.thumbnail_url}
          className="w-full h-full object-contain max-h-[calc(100vh-3.5rem)] lg:max-h-screen"
          loop
          playsInline
          onClick={togglePlay}
          onTimeUpdate={handleTimeUpdate}
          data-testid={`video-player-${video.id}`}
        />

        {/* Play/Pause Overlay */}
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
              onClick={togglePlay}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-white/90 shadow-2xl flex items-center justify-center"
              >
                <Play className="w-10 h-10 text-[#1E4ED8] ml-1" fill="#1E4ED8" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Video Counter & Sound */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <Badge className="bg-black/50 text-white border-0 backdrop-blur-sm">
            {index + 1} / {total}
          </Badge>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Overlay Info */}
        <div className="lg:hidden absolute bottom-0 left-0 right-16 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          {/* Creator Info */}
          <Link to={`/u/${video.username}`} className="flex items-center gap-3 mb-3">
            <div className="p-0.5 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6]">
              <Avatar className="w-10 h-10 border-2 border-black">
                <AvatarImage src={video.profile_picture} />
                <AvatarFallback className="bg-[#1E4ED8] text-white font-bold">
                  {video.display_name?.charAt(0) || video.username?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{video.display_name}</span>
                {video.is_verified && <Sparkles className="w-4 h-4 text-[#3B82F6]" />}
              </div>
              <span className="text-white/70 text-sm">@{video.username}</span>
            </div>
            {!video.is_following && (
              <Button
                onClick={(e) => { e.preventDefault(); onFollow(video.user_id); }}
                size="sm"
                className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 text-white rounded-full px-4"
              >
                {t('common.follow')}
              </Button>
            )}
          </Link>

          {/* Caption */}
          <p className="text-white text-sm mb-2 line-clamp-2">{video.caption}</p>

          {/* Hashtags */}
          {video.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {video.hashtags.slice(0, 3).map((tag, i) => (
                <span key={i} className="text-white/80 text-xs">#{tag}</span>
              ))}
            </div>
          )}

          {/* Music */}
          {video.music_name && (
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <Music2 className="w-4 h-4" />
              <span className="truncate">{video.music_name}</span>
            </div>
          )}
        </div>

        {/* Mobile Right Actions */}
        <div className="lg:hidden absolute right-2 bottom-24 flex flex-col items-center gap-4">
          {/* Like */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => onLike(video.id)}
            className="flex flex-col items-center"
            data-testid={`like-btn-${video.id}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              video.is_liked ? 'bg-[#D9381E]' : 'bg-black/50 backdrop-blur-sm'
            }`}>
              <Heart className={`w-6 h-6 text-white ${video.is_liked ? 'fill-white' : ''}`} />
            </div>
            <span className="text-white text-xs font-medium mt-1">{formatCount(video.likes_count)}</span>
          </motion.button>

          {/* Comment */}
          <button
            onClick={() => onComment(video.id)}
            className="flex flex-col items-center"
            data-testid={`comment-btn-${video.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs font-medium mt-1">{formatCount(video.comments_count)}</span>
          </button>

          {/* Save */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => onSave(video.id)}
            className="flex flex-col items-center"
            data-testid={`save-btn-${video.id}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              video.is_saved ? 'bg-[#1E4ED8]' : 'bg-black/50 backdrop-blur-sm'
            }`}>
              <Bookmark className={`w-6 h-6 text-white ${video.is_saved ? 'fill-white' : ''}`} />
            </div>
            <span className="text-white text-xs font-medium mt-1">{t('feed.save')}</span>
          </motion.button>

          {/* Share */}
          <button
            onClick={() => onShare(video.id)}
            className="flex flex-col items-center"
            data-testid={`share-btn-${video.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs font-medium mt-1">{t('feed.share')}</span>
          </button>

          {/* Report */}
          <button
            onClick={() => onReport(video)}
            className="flex flex-col items-center"
            data-testid={`report-btn-${video.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <Flag className="w-5 h-5 text-white" />
            </div>
            <span className="text-white text-xs font-medium mt-1">Signaler</span>
          </button>
        </div>
      </div>

      {/* Desktop Side Panel */}
      <div className="hidden lg:flex w-96 bg-white flex-col border-l border-gray-100">
        {/* Creator Info */}
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <Link to={`/u/${video.username}`} className="flex items-center gap-3 group flex-1">
              <div className="relative">
                <div className="p-0.5 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6]">
                  <Avatar className="w-14 h-14 border-2 border-white">
                    <AvatarImage src={video.profile_picture} />
                    <AvatarFallback className="bg-[#1E4ED8]/10 text-[#1E4ED8] font-bold text-lg">
                      {video.display_name?.charAt(0) || video.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                {video.is_verified && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1E4ED8] flex items-center justify-center border-2 border-white">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 group-hover:text-[#1E4ED8] transition-colors truncate">
                  {video.display_name}
                </p>
                <p className="text-sm text-gray-500">@{video.username}</p>
              </div>
            </Link>
            
            {!video.is_following ? (
              <Button
                onClick={() => onFollow(video.user_id)}
                size="sm"
                className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 text-white rounded-full px-5"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('common.follow')}
              </Button>
            ) : (
              <Badge className="bg-[#1E4ED8]/10 text-[#1E4ED8]">
                <Users className="w-3.5 h-3.5 mr-1" />
                {t('common.following')}
              </Badge>
            )}
          </div>

          {/* BARKA & Level */}
          <div className="mt-4 flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#D9381E]/10 to-[#E07A5F]/10 rounded-full px-4 py-1.5">
              <Flame className="w-4 h-4 text-[#D9381E]" />
              <span className="font-bold text-[#D9381E]">{video.barka}</span>
              <span className="text-xs text-[#D9381E]/70">BARKA</span>
            </div>
            <Badge variant="outline" className="border-[#1E4ED8]/30 text-[#1E4ED8] capitalize">
              {video.user_level?.replace('_', ' ')}
            </Badge>
            <div className="ml-auto flex items-center gap-1.5 text-gray-500 text-sm">
              <Eye className="w-4 h-4" />
              {formatCount(video.views_count)} {t('common.views').toLowerCase()}
            </div>
          </div>
        </div>

        {/* Caption & Tags */}
        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-gray-700 leading-relaxed text-[15px]">
            {video.caption}
          </p>

          {/* Hashtags */}
          {video.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {video.hashtags.map((tag, i) => (
                <Badge 
                  key={i} 
                  className="bg-[#1E4ED8]/10 text-[#1E4ED8] hover:bg-[#1E4ED8] hover:text-white transition-colors cursor-pointer"
                >
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-3 mt-5 text-sm text-gray-500">
            {video.region && (
              <span className="flex items-center gap-1.5 bg-[#F8F5F2] rounded-full px-3 py-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#1E4ED8]" />
                {video.region.replace('_', ' ')}
              </span>
            )}
            {video.music_name && (
              <span className="flex items-center gap-1.5 bg-[#F8F5F2] rounded-full px-3 py-1.5">
                <Music2 className="w-3.5 h-3.5 text-[#1E4ED8]" />
                {video.music_name}
              </span>
            )}
          </div>

          {/* Recipe Link */}
          {video.recipe_id && (
            <Link
              to={`/recipe/${video.recipe_id}`}
              className="mt-5 flex items-center gap-3 p-4 bg-gradient-to-r from-[#1E4ED8]/5 to-[#3B82F6]/5 rounded-2xl border border-[#1E4ED8]/20 hover:border-[#1E4ED8]/40 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-[#1E4ED8] flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{t('feed.seeFullRecipe')}</p>
                <p className="text-sm text-gray-500">{t('feed.ingredientsAndSteps')}</p>
              </div>
            </Link>
          )}
        </div>

        {/* Desktop Actions Bar */}
        <div className="p-5 border-t border-gray-100 bg-[#F8F5F2]">
          <div className="flex items-center justify-between">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => onLike(video.id)}
              className="flex flex-col items-center gap-1"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                video.is_liked 
                  ? 'bg-[#D9381E] text-white shadow-lg shadow-[#D9381E]/30' 
                  : 'bg-white text-gray-500 hover:text-[#D9381E] border border-gray-200 hover:border-[#D9381E]'
              }`}>
                <Heart className={`w-7 h-7 ${video.is_liked ? 'fill-current' : ''}`} />
              </div>
              <span className={`text-xs font-semibold ${video.is_liked ? 'text-[#D9381E]' : 'text-gray-500'}`}>
                {formatCount(video.likes_count)}
              </span>
            </motion.button>

            <button onClick={() => onComment(video.id)} className="flex flex-col items-center gap-1">
              <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#1E4ED8] hover:border-[#1E4ED8] transition-all">
                <MessageCircle className="w-7 h-7" />
              </div>
              <span className="text-xs font-semibold text-gray-500">{formatCount(video.comments_count)}</span>
            </button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => onSave(video.id)}
              className="flex flex-col items-center gap-1"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                video.is_saved 
                  ? 'bg-[#1E4ED8] text-white shadow-lg shadow-[#1E4ED8]/30' 
                  : 'bg-white text-gray-500 hover:text-[#1E4ED8] border border-gray-200 hover:border-[#1E4ED8]'
              }`}>
                <Bookmark className={`w-7 h-7 ${video.is_saved ? 'fill-current' : ''}`} />
              </div>
              <span className={`text-xs font-semibold ${video.is_saved ? 'text-[#1E4ED8]' : 'text-gray-500'}`}>
                {t('feed.save')}
              </span>
            </motion.button>

            <button onClick={() => onShare(video.id)} className="flex flex-col items-center gap-1">
              <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#1E4ED8] hover:border-[#1E4ED8] transition-all">
                <Share2 className="w-7 h-7" />
              </div>
              <span className="text-xs font-semibold text-gray-500">{t('feed.share')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Comments Sheet Component
const CommentsSheet = ({ video, isOpen, onClose, api, user, t }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && video) {
      fetchComments();
    }
  }, [isOpen, video]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/videos/${video.id}/comments`);
      setComments(res.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const res = await api.post(`/videos/${video.id}/comments`, {
        content: newComment,
        user_id: user.id
      });
      setComments([res.data.comment, ...comments]);
      setNewComment('');
      toast.success(t('success.commentAdded'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      className="fixed inset-x-0 bottom-0 h-[70vh] bg-white rounded-t-3xl z-50 flex flex-col shadow-2xl border-t-4 border-[#1E4ED8]"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1E4ED8]/10 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#1E4ED8]" />
          </div>
          <h3 className="font-semibold text-lg text-gray-900">
            {video?.comments_count || 0} commentaires
          </h3>
        </div>
        <button 
          onClick={onClose} 
          className="w-10 h-10 rounded-full bg-[#F8F5F2] flex items-center justify-center hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8F5F2]">
        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-[#1E4ED8]" />
            </div>
            <p className="text-gray-500">{t('video.beFirstComment')}</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 bg-white rounded-2xl p-4 shadow-sm">
              <Avatar className="w-10 h-10">
                <AvatarImage src={comment.profile_picture} />
                <AvatarFallback className="bg-[#1E4ED8]/10 text-[#1E4ED8]">
                  {comment.username?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{comment.display_name}</span>
                  <span className="text-xs text-gray-400">@{comment.username}</span>
                </div>
                <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span>{new Date(comment.created_at).toLocaleDateString('fr-FR')}</span>
                  <button className="flex items-center gap-1 hover:text-[#D9381E] transition-colors">
                    <Heart className="w-3 h-3" />
                    {comment.likes_count}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comment Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-100 bg-white flex gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={user?.profile_picture} />
          <AvatarFallback className="bg-[#1E4ED8]/10 text-[#1E4ED8]">
            {user?.username?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('video.addComment')}
            className="flex-1 bg-[#F8F5F2] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4ED8]/50 border border-gray-200"
          />
          <Button 
            type="submit" 
            size="icon"
            className="rounded-full bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 w-10 h-10"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

// Main Video Feed Page
export default function VideoFeedPage() {
  const { api, user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: videoId } = useParams();
  const containerRef = useRef(null);
  
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [feedType, setFeedType] = useState('fyp');
  const [showComments, setShowComments] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [reportTarget, setReportTarget] = useState(null);

  const fetchVideos = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      const endpoint = feedType === 'fyp' 
        ? `/videos/fyp?limit=10&offset=${currentOffset}${user ? `&user_id=${user.id}` : ''}`
        : `/videos/following?user_id=${user?.id}&limit=10&offset=${currentOffset}`;
      
      const res = await api.get(endpoint);
      const newVideos = res.data.videos || [];
      
      if (reset) {
        setVideos(newVideos);
        setOffset(10);
      } else {
        setVideos(prev => [...prev, ...newVideos]);
        setOffset(prev => prev + 10);
      }
      
      setHasMore(res.data.has_more);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  }, [api, feedType, offset, user]);

  useEffect(() => {
    setLoading(true);
    setActiveIndex(0);
    fetchVideos(true);
  }, [feedType]);

  // Handle navigation (wheel, touch, keyboard)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startY = 0;
    let isDragging = false;

    const goNext = () => {
      if (activeIndex < videos.length - 1) {
        setActiveIndex(prev => prev + 1);
        if (activeIndex >= videos.length - 3 && hasMore) {
          fetchVideos();
        }
      }
    };

    const goPrev = () => {
      if (activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      }
    };

    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    };

    const handleTouchEnd = (e) => {
      if (!isDragging) return;
      const endY = e.changedTouches[0].clientY;
      const diff = startY - endY;

      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goPrev();
      }
      isDragging = false;
    };

    const handleWheel = (e) => {
      if (e.deltaY > 0) goNext();
      else if (e.deltaY < 0) goPrev();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') goNext();
      else if (e.key === 'ArrowUp') goPrev();
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('wheel', handleWheel);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, videos.length, hasMore]);

  // Actions
  const handleLike = async (videoId) => {
    try {
      const res = await api.post(`/videos/${videoId}/like`, { user_id: user.id });
      setVideos(prev => prev.map(v => 
        v.id === videoId 
          ? { ...v, is_liked: res.data.liked, likes_count: res.data.likes_count }
          : v
      ));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSave = async (videoId) => {
    try {
      const res = await api.post(`/videos/${videoId}/save`, { user_id: user.id });
      setVideos(prev => prev.map(v => 
        v.id === videoId 
          ? { ...v, is_saved: res.data.saved }
          : v
      ));
      toast.success(res.data.saved ? t('video.videoSaved') : t('video.videoRemoved'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleShare = async (videoId) => {
    const video = videos.find(v => v.id === videoId);
    const shareUrl = `${window.location.origin}/video/${videoId}`;
    const shareTitle = video?.caption || 'MHARHER - Cuisine Tunisienne';
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Regardez cette vidéo sur MHARHER!`,
          url: shareUrl,
        });
        // Track share
        await api.post(`/videos/${videoId}/share`).catch(() => {});
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      await api.post(`/videos/${videoId}/share`).catch(() => {});
      toast.success(t('share.copied', 'Lien copié!'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleFollow = async (userId) => {
    try {
      await api.post(`/users/${userId}/follow`);
      setVideos(prev => prev.map(v => 
        v.user_id === userId ? { ...v, is_following: true } : v
      ));
      toast.success(t('success.subscribed'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleComment = (videoId) => {
    setSelectedVideo(videos.find(v => v.id === videoId));
    setShowComments(true);
  };

  if (!isAuthenticated) {
    navigate('/auth');
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className="h-[calc(100vh-3.5rem)] lg:h-screen overflow-hidden bg-black"
      data-testid="video-feed-page"
    >
      {/* Feed Type Toggle */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
        <div className="flex bg-black/50 backdrop-blur-xl rounded-full p-1">
          <button
            onClick={() => setFeedType('following')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              feedType === 'following' 
                ? 'bg-white text-gray-900' 
                : 'text-white/70 hover:text-white'
            }`}
          >
            {t('feed.following')}
          </button>
          <button
            onClick={() => setFeedType('fyp')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
              feedType === 'fyp' 
                ? 'bg-white text-gray-900' 
                : 'text-white/70 hover:text-white'
            }`}
          >
            <Flame className="w-4 h-4" />
            {t('feed.forYou')}
          </button>
        </div>
      </div>

      {/* Videos */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 rounded-full border-4 border-white/20 border-t-white"
          />
          <p className="text-white/70">{t('feed.loadingVideos')}</p>
        </div>
      ) : videos.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-24 h-24 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
            <ChefHat className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {feedType === 'following' ? t('feed.noVideos') : t('feed.noVideos')}
          </h2>
          <p className="text-white/60 max-w-xs mb-6">
            {feedType === 'following' 
              ? t('feed.followCreators')
              : t('feed.beFirst')}
          </p>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/explore')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-full"
            >
              {t('nav.explore')}
            </Button>
            <Button 
              onClick={() => navigate('/create-video')}
              className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 rounded-full"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('feed.createVideo')}
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateY(-${activeIndex * 100}%)` }}
        >
          {videos.map((video, index) => (
            <div 
              key={video.id} 
              className="h-full w-full"
              style={{ height: 'calc(100vh - 3.5rem)' }}
            >
              <VideoCard
                video={video}
                isActive={index === activeIndex}
                onLike={handleLike}
                onSave={handleSave}
                onShare={handleShare}
                onFollow={handleFollow}
                onComment={handleComment}
                onReport={(v) => setReportTarget({ type: 'video', id: v.id, name: v.caption?.slice(0, 50) || 'Vidéo' })}
                isMuted={isMuted}
                onToggleMute={() => setIsMuted(!isMuted)}
                index={index}
                total={videos.length}
                t={t}
              />
            </div>
          ))}
        </div>
      )}

      {/* Navigation Hints - Desktop */}
      <div className="hidden lg:flex absolute bottom-6 left-6 flex-col gap-2 z-20">
        <button 
          onClick={() => activeIndex > 0 && setActiveIndex(prev => prev - 1)}
          disabled={activeIndex === 0}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            activeIndex > 0 
              ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30' 
              : 'bg-white/5 text-white/30 cursor-not-allowed'
          }`}
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button 
          onClick={() => activeIndex < videos.length - 1 && setActiveIndex(prev => prev + 1)}
          disabled={activeIndex === videos.length - 1}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            activeIndex < videos.length - 1 
              ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30' 
              : 'bg-white/5 text-white/30 cursor-not-allowed'
          }`}
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* Comments Sheet */}
      <AnimatePresence>
        {showComments && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowComments(false)}
            />
            <CommentsSheet
              video={selectedVideo}
              isOpen={showComments}
              onClose={() => setShowComments(false)}
              api={api}
              user={user}
              t={t}
            />
          </>
        )}
      </AnimatePresence>
      
      {/* Report Dialog */}
      {reportTarget && (
        <ReportDialog
          open={!!reportTarget}
          onOpenChange={(open) => !open && setReportTarget(null)}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetName={reportTarget.name}
        />
      )}
    </div>
  );
}
